import { useMemo } from 'react';

import { tenantType, userType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useAnalyticsAttributes } from './context';

type ErrorDetails = {
	message: string;
	stack?: string;
};

export type AnalyticsAttributes = Record<
	string,
	string | number | boolean | ErrorDetails | undefined | object
>;

type AnalyticsEmit = (
	actionSubject: string,
	action: string,
	attributes?: AnalyticsAttributes,
	actionSubjectId?: string,
) => void;

export type ScreenAnalyticsEvent = {
	name: string;
	attributes?: AnalyticsAttributes;
};

type ScreenAnalyticsEmit = (event: ScreenAnalyticsEvent) => void;

export type AnalyticsApi = {
	track: AnalyticsEmit;
	ui: AnalyticsEmit;
	operational: AnalyticsEmit;
	screen: ScreenAnalyticsEmit;
};

export const useAnalytics = (): AnalyticsApi => {
	const baseAnalyticsAttributes = useAnalyticsAttributes();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useMemo<AnalyticsApi>(() => {
		const fire =
			(eventType: 'track' | 'ui' | 'operational'): AnalyticsEmit =>
			(actionSubject, action, attributes, actionSubjectId) => {
				createAnalyticsEvent({
					action,
					actionSubject,
					actionSubjectId,
					eventType,
					tags: ['townsquare'],
					attributes: { ...baseAnalyticsAttributes, ...attributes },
				}).fire('atlas');
			};
		const screen: ScreenAnalyticsEmit = ({ name, attributes }) => {
			createAnalyticsEvent({
				eventType: 'screen',
				name,
				tags: ['townsquare'],
				attributes: { ...baseAnalyticsAttributes, ...attributes },
			}).fire('atlas');
		};

		return {
			track: fire('track'),
			ui: fire('ui'),
			operational: fire('operational'),
			screen,
		};
	}, [baseAnalyticsAttributes, createAnalyticsEvent]);
};

/*
 * NOTE: at least one of accountId and cloudId must be provided in order for analytics
 * to be sent successfully.
 */
export function setAnalyticsClientInfo(
	analyticsClient: AnalyticsWebClient,
	subproduct: 'townsquare-goals' | 'townsquare-projects',
	accountId?: string,
	orgId?: string,
	cloudId?: string,
): void {
	analyticsClient.setSubproduct(subproduct);

	if (orgId && orgId !== '') {
		analyticsClient.setOrgInfo(orgId);
	}

	if (cloudId && cloudId !== '') {
		analyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	} else {
		analyticsClient.setTenantInfo(tenantType.NONE, cloudId);
	}

	if (accountId && accountId !== '') {
		analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
	}
}
